import React, { useState, useEffect, useContext, useCallback, useRef } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Table,
  Button,
  Form,
  InputGroup,
  Spinner,
  Badge,
  Modal,
  Alert,
  Pagination,
  Tooltip,
  OverlayTrigger,
  Dropdown,
} from "react-bootstrap";
import {
  FaEye,
  FaEdit,
  FaTrash,
  FaFilter,
  FaSearch,
  FaSort,
  FaSortUp,
  FaSortDown,
  FaDownload,
  FaPrint,
  FaSync,
  FaMoneyBillWave,
  FaCalendar,
  FaUser,
  FaFileAlt,
  FaHandHoldingHeart,
  FaHospital,
  FaHeart,
  FaRing,
  FaBaby,
  FaExclamationTriangle,
  FaChartBar,
  FaCheckCircle,
  FaClock,
  FaTimesCircle,
  FaFileExcel,
  FaFileCsv,
  FaArrowRight,
  FaArrowLeft,
  FaUsers,
  FaMoneyCheck,
  FaPercentage,
  FaChild,
} from "react-icons/fa";
import { toast } from "react-toastify";
import axios from "axios";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { AuthContext } from "../../../contexts/AuthContext";

const BenefitsList = () => {
  const { user, token } = useContext(AuthContext);
  const [benefits, setBenefits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedBenefit, setSelectedBenefit] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    type: "",
    status: "",
    start_date: "",
    end_date: "",
    search: "",
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
  });
  const [sortConfig, setSortConfig] = useState({
    key: "created_at",
    direction: "desc",
  });
  const [stats, setStats] = useState({
    total: 0,
    totalAmount: 0,
    averageAmount: 0,
    totalMembers: 0,
    byType: {
      FUNERAL: { count: 0, amount: 0 },
      MEDICAL: { count: 0, amount: 0 },
      MARRIAGE: { count: 0, amount: 0 },
      BIRTH: { count: 0, amount: 0 },
      EMERGENCY: { count: 0, amount: 0 },
      OTHER: { count: 0, amount: 0 },
    },
  });
  const [exportLoading, setExportLoading] = useState(false);
  const [lastRefreshed, setLastRefreshed] = useState(new Date());
  const [autoRefresh, setAutoRefresh] = useState(false);
  const refreshInterval = useRef(null);

  // Benefit type configuration
  const benefitTypes = {
    FUNERAL: {
      label: "Funeral",
      icon: <FaHeart className="me-1" />,
      color: "danger",
      bgColor: "rgba(220, 53, 69, 0.1)",
    },
    MEDICAL: {
      label: "Medical",
      icon: <FaHospital className="me-1" />,
      color: "primary",
      bgColor: "rgba(13, 110, 253, 0.1)",
    },
    MARRIAGE: {
      label: "Marriage",
      icon: <FaRing className="me-1" />,
      color: "success",
      bgColor: "rgba(25, 135, 84, 0.1)",
    },
    BIRTH: {
      label: "Birth",
      icon: <FaBaby className="me-1" />,
      color: "info",
      bgColor: "rgba(13, 202, 240, 0.1)",
    },
    EMERGENCY: {
      label: "Emergency",
      icon: <FaExclamationTriangle className="me-1" />,
      color: "warning",
      bgColor: "rgba(255, 193, 7, 0.1)",
    },
    OTHER: {
      label: "Other",
      icon: <FaHandHoldingHeart className="me-1" />,
      color: "secondary",
      bgColor: "rgba(108, 117, 125, 0.1)",
    },
  };

  // Status configuration
  const statusConfig = {
    APPROVED: {
      label: "Approved",
      color: "success",
      icon: <FaCheckCircle className="me-1" />,
    },
    PENDING: {
      label: "Pending",
      color: "warning",
      icon: <FaClock className="me-1" />,
    },
    REJECTED: {
      label: "Rejected",
      color: "danger",
      icon: <FaTimesCircle className="me-1" />,
    },
  };

  // ================== DATA FETCHING FUNCTIONS ==================

  // Fetch paginated benefits
  const fetchBenefits = useCallback(async (showLoading = true) => {
    if (showLoading) setLoading(true);
    setError("");

    try {
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        sort: sortConfig.key,
        order: sortConfig.direction,
        _t: Date.now(), // Cache busting
        ...filters,
      };

      // Remove empty filters
      Object.keys(params).forEach(
        (key) => params[key] === "" && delete params[key]
      );

      const response = await axios.get(
        "https://aceswafund.org/welfare/welfare-system/admin-list-benefit",
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Cache-Control': 'no-cache',
            'Pragma': 'no-cache',
            'Expires': '0',
          },
          params,
        }
      );

      if (response.data.status) {
        setBenefits(response.data.data.benefits || []);
        setPagination(response.data.data.pagination || {
          page: 1,
          limit: 10,
          total: 0,
          totalPages: 0
        });
        setLastRefreshed(new Date());
      } else {
        setError(response.data.message || "Failed to fetch benefits");
      }
    } catch (err) {
      console.error("Error fetching benefits:", err);
      setError(err.response?.data?.message || "Network error occurred");
      toast.error("Failed to load benefits");
    } finally {
      if (showLoading) setLoading(false);
    }
  }, [pagination.page, pagination.limit, sortConfig.key, sortConfig.direction, filters, token]);

  // Fetch all benefits for statistics
  const fetchStatistics = useCallback(async () => {
    setStatsLoading(true);
    try {
      const params = {
        limit: 10000,
        _t: Date.now(),
        ...filters,
      };

      const response = await axios.get(
        "https://aceswafund.org/welfare/welfare-system/admin-list-benefit",
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Cache-Control': 'no-cache',
            'Pragma': 'no-cache',
            'Expires': '0',
          },
          params,
        }
      );

      if (response.data.status) {
        const allBenefits = response.data.data.benefits || [];
        calculateDetailedStats(allBenefits);
      }
    } catch (error) {
      console.error("Error fetching statistics:", error);
      toast.error("Failed to load statistics");
    } finally {
      setStatsLoading(false);
    }
  }, [filters, token]);

  // Combined refresh function
  const refreshAllData = useCallback(async (showToast = true) => {
    try {
      await Promise.all([
        fetchBenefits(true),
        fetchStatistics()
      ]);
      if (showToast) {
        toast.success(`Data refreshed successfully at ${new Date().toLocaleTimeString()}`);
      }
    } catch (error) {
      console.error("Error refreshing data:", error);
      if (showToast) {
        toast.error("Failed to refresh data");
      }
    }
  }, [fetchBenefits, fetchStatistics]);

  // Auto-refresh setup
  useEffect(() => {
    if (autoRefresh) {
      refreshInterval.current = setInterval(() => {
        refreshAllData(false);
      }, 30000);
    }

    return () => {
      if (refreshInterval.current) {
        clearInterval(refreshInterval.current);
      }
    };
  }, [autoRefresh, refreshAllData]);

  // Initial fetch and when dependencies change
  useEffect(() => {
    refreshAllData(false);
  }, [pagination.page, sortConfig.key, sortConfig.direction, JSON.stringify(filters)]);

  // ================== STATISTICS CALCULATION ==================

  // Calculate detailed statistics
  const calculateDetailedStats = (benefitsList) => {
    const uniqueMembers = new Set(benefitsList.map(b => b.member_id || b.member_email));

    const newStats = {
      total: benefitsList.length,
      totalAmount: 0,
      averageAmount: 0,
      totalMembers: uniqueMembers.size,
      byType: {
        FUNERAL: { count: 0, amount: 0 },
        MEDICAL: { count: 0, amount: 0 },
        MARRIAGE: { count: 0, amount: 0 },
        BIRTH: { count: 0, amount: 0 },
        EMERGENCY: { count: 0, amount: 0 },
        OTHER: { count: 0, amount: 0 },
      },
    };

    benefitsList.forEach((benefit) => {
      const amount = parseFloat(benefit.amount) || 0;
      const type = benefit.type || "OTHER";

      newStats.totalAmount += amount;

      if (newStats.byType[type]) {
        newStats.byType[type].count += 1;
        newStats.byType[type].amount += amount;
      } else {
        newStats.byType.OTHER.count += 1;
        newStats.byType.OTHER.amount += amount;
      }
    });

    newStats.averageAmount = newStats.total > 0
      ? newStats.totalAmount / newStats.total
      : 0;

    setStats(newStats);
  };

  // ================== EXPORT FUNCTIONALITY ==================

  // Fetch all benefits for export
  const fetchAllBenefitsForExport = async () => {
    try {
      const params = {
        limit: 10000,
        sort: "created_at",
        order: "desc",
        _t: Date.now(),
        ...filters,
      };

      Object.keys(params).forEach(
        (key) => params[key] === "" && delete params[key]
      );
      delete params.page;

      const response = await axios.get(
        "https://aceswafund.org/welfare/welfare-system/admin-list-benefit",
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            'Pragma': 'no-cache',
            'Expires': '0'
          },
          params,
        }
      );

      return response.data.status ? response.data.data.benefits : [];
    } catch (error) {
      console.error("Error fetching benefits for export:", error);
      toast.error("Failed to fetch data for export");
      return [];
    }
  };

  // Format data for export
  const formatDataForExport = (benefitsData) => {
    return benefitsData.map((benefit, index) => ({
      "#": index + 1,
      "Benefit ID": benefit.id,
      "Member Name": benefit.member_name,
      "Member Email": benefit.member_email,
      "Member Phone": benefit.member_phone,
      "Benefit Type": benefitTypes[benefit.type]?.label || benefit.type,
      "Amount": `GHS ${parseFloat(benefit.amount).toFixed(2)}`,
      "Status": "Approved",
      "Description": benefit.description || "N/A",
      "Created By": benefit.created_by_name || "N/A",
      "Approved By": benefit.approved_by_name || "N/A",
      "Created Date": formatDateForExport(benefit.created_at),
      "Approved Date": benefit.approved_at ? formatDateForExport(benefit.approved_at) : "N/A",
    }));
  };

  // Format date for export
  const formatDateForExport = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Export to Excel
  const exportToExcel = async (format = "xlsx") => {
    if (exportLoading) return;

    setExportLoading(true);

    try {
      toast.info("Preparing export data...");

      const allBenefits = await fetchAllBenefitsForExport();

      if (allBenefits.length === 0) {
        toast.warning("No data to export");
        setExportLoading(false);
        return;
      }

      const exportData = formatDataForExport(allBenefits);

      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(exportData);
      XLSX.utils.book_append_sheet(wb, ws, "Benefits");

      const summaryData = [
        ["BENEFITS SUMMARY REPORT"],
        ["Generated", new Date().toLocaleString()],
        ["Total Benefits", stats.total],
        ["Total Amount", `GHS ${stats.totalAmount.toFixed(2)}`],
        ["Average Amount", `GHS ${stats.averageAmount.toFixed(2)}`],
        ["Total Members", stats.totalMembers],
        [],
        ["BENEFITS BY TYPE"],
        ["Type", "Count", "Amount"],
        ...Object.entries(stats.byType).map(([type, data]) => [
          benefitTypes[type]?.label || type,
          data.count,
          `GHS ${data.amount.toFixed(2)}`,
        ]),
        [],
        ["FILTERS APPLIED"],
        ["Benefit Type", filters.type || "All"],
        ["Status", "Approved (All)"],
        [
          "Date Range",
          filters.start_date && filters.end_date
            ? `${filters.start_date} to ${filters.end_date}`
            : "All dates",
        ],
        ["Search Term", filters.search || "None"],
      ];

      const wsSummary = XLSX.utils.aoa_to_sheet(summaryData);
      XLSX.utils.book_append_sheet(wb, wsSummary, "Summary");

      const timestamp = new Date()
        .toISOString()
        .slice(0, 19)
        .replace(/[:]/g, "-");
      const fileName = `Benefits_Export_${timestamp}.${format}`;

      if (format === "xlsx") {
        XLSX.writeFile(wb, fileName);
      } else if (format === "csv") {
        const csv = XLSX.utils.sheet_to_csv(ws);
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
        saveAs(blob, fileName);
      }

      toast.success(`Exported ${exportData.length} benefits to ${fileName}`);
    } catch (error) {
      console.error("Export error:", error);
      toast.error("Failed to export benefits");
    } finally {
      setExportLoading(false);
    }
  };

  // Quick export current page
  const exportCurrentPage = () => {
    if (benefits.length === 0) {
      toast.warning("No data to export");
      return;
    }

    try {
      const exportData = formatDataForExport(benefits);
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(exportData);
      XLSX.utils.book_append_sheet(wb, ws, "Benefits");

      const timestamp = new Date()
        .toISOString()
        .slice(0, 19)
        .replace(/[:]/g, "-");
      const fileName = `Benefits_Page_${pagination.page}_${timestamp}.xlsx`;

      XLSX.writeFile(wb, fileName);
      toast.success(
        `Exported page ${pagination.page} (${exportData.length} records)`
      );
    } catch (error) {
      console.error("Quick export error:", error);
      toast.error("Failed to export current page");
    }
  };

  // Print benefits
  const printBenefits = () => {
    const printWindow = window.open("", "_blank");
    printWindow.document.write(`
      <html>
        <head>
          <title>Benefits Report</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; }
            h1 { color: #2c3e50; border-bottom: 2px solid #3498db; padding-bottom: 10px; }
            .summary-box { background-color: #f8f9fa; padding: 15px; border-radius: 5px; margin-bottom: 20px; }
            .stats-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 15px; margin-bottom: 20px; }
            .stat-item { background: white; padding: 15px; border-radius: 5px; box-shadow: 0 2px 4px rgba(0,0,0,0.1); }
            table { width: 100%; border-collapse: collapse; margin-top: 20px; }
            th { background-color: #f8f9fa; border: 1px solid #dee2e6; padding: 8px; text-align: left; }
            td { border: 1px solid #dee2e6; padding: 8px; }
            .footer { margin-top: 30px; font-size: 12px; color: #6c757d; text-align: center; }
          </style>
        </head>
        <body>
          <h1>Benefits Report</h1>
          
          <div class="summary-box">
            <strong>Generated:</strong> ${new Date().toLocaleString()}<br>
            <strong>Filters:</strong> ${filters.type ? `Type: ${benefitTypes[filters.type]?.label || filters.type}` : "All Types"} | 
            ${filters.start_date && filters.end_date ? `Date: ${filters.start_date} to ${filters.end_date}` : "All Dates"}
          </div>

          <div class="stats-grid">
            <div class="stat-item">
              <strong>Total Benefits</strong>
              <h3>${stats.total}</h3>
            </div>
            <div class="stat-item">
              <strong>Total Amount</strong>
              <h3>GHS ${stats.totalAmount.toFixed(2)}</h3>
            </div>
            <div class="stat-item">
              <strong>Average Amount</strong>
              <h3>GHS ${stats.averageAmount.toFixed(2)}</h3>
            </div>
            <div class="stat-item">
              <strong>Total Members</strong>
              <h3>${stats.totalMembers}</h3>
            </div>
          </div>

          <table>
            <thead>
              <tr>
                <th>#</th>
                <th>Member Name</th>
                <th>Benefit Type</th>
                <th>Amount</th>
                <th>Description</th>
                <th>Created Date</th>
              </tr>
            </thead>
            <tbody>
              ${benefits
        .map(
          (benefit, index) => `
                <tr>
                  <td>${index + 1}</td>
                  <td>${benefit.member_name}</td>
                  <td>${benefitTypes[benefit.type]?.label || benefit.type}</td>
                  <td>GHS ${parseFloat(benefit.amount).toFixed(2)}</td>
                  <td>${benefit.description || "N/A"}</td>
                  <td>${formatDate(benefit.created_at)}</td>
                </tr>
              `
        )
        .join("")}
            </tbody>
          </table>
          <div class="footer">
            ACESWA Benefits System &copy; ${new Date().getFullYear()} | Printed on: ${new Date().toLocaleString()}
          </div>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
    }, 250);
  };

  // ================== UTILITY FUNCTIONS ==================

  // Handle filter changes
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Apply filters
  const applyFilters = () => {
    setPagination((prev) => ({ ...prev, page: 1 }));
    setShowFilters(false);
  };

  // Reset filters
  const resetFilters = () => {
    setFilters({
      type: "",
      status: "",
      start_date: "",
      end_date: "",
      search: "",
    });
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  // Handle sort
  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  // Handle pagination
  const handlePageChange = (page) => {
    setPagination((prev) => ({ ...prev, page }));
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-GH", {
      style: "currency",
      currency: "GHS",
      minimumFractionDigits: 2,
    }).format(amount);
  };

  // View benefit details
  const viewBenefitDetails = (benefit) => {
    setSelectedBenefit(benefit);
    setShowDetailModal(true);
  };

  // Refresh benefits
  const refreshBenefits = () => {
    refreshAllData(true);
  };

  // Render sort icon
  const renderSortIcon = (key) => {
    if (sortConfig.key !== key) return <FaSort className="text-muted" />;
    return sortConfig.direction === "asc" ? (
      <FaSortUp className="text-primary" />
    ) : (
      <FaSortDown className="text-primary" />
    );
  };

  // Add CSS for spin animation
  const spinAnimation = `
    @keyframes spin {
      from { transform: rotate(0deg); }
      to { transform: rotate(360deg); }
    }
    .spin {
      animation: spin 2s linear infinite;
    }
  `;

  return (
    <Container fluid className="py-4">
      <style>{spinAnimation}</style>

      {/* Header */}
      <Row className="mb-4">
        <Col>
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h2 className="mb-0">
                <FaHandHoldingHeart className="me-2 text-primary" />
                Benefits Management
              </h2>
              <p className="text-muted mb-0">
                View and manage all approved benefit records
              </p>
              <small className="text-muted">
                Last refreshed: {lastRefreshed.toLocaleTimeString()}
              </small>
            </div>
            <div className="d-flex gap-2">
              {/* Auto-refresh toggle */}
              <OverlayTrigger
                placement="bottom"
                overlay={<Tooltip>Toggle auto-refresh (30s)</Tooltip>}
              >
                <Button
                  variant={autoRefresh ? "success" : "outline-secondary"}
                  onClick={() => setAutoRefresh(!autoRefresh)}
                  className="d-flex align-items-center"
                >
                  <FaSync className={autoRefresh ? "spin" : ""} />
                  {autoRefresh && <span className="ms-2 small">Auto</span>}
                </Button>
              </OverlayTrigger>

              {/* Export Dropdown */}
              <Dropdown>
                <Dropdown.Toggle
                  variant="success"
                  id="dropdown-export"
                  className="d-flex align-items-center"
                  disabled={exportLoading}
                >
                  <FaFileExcel className="me-2" />
                  Export
                  {exportLoading && (
                    <Spinner animation="border" size="sm" className="ms-2" />
                  )}
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  <Dropdown.Item onClick={() => exportToExcel("xlsx")}>
                    <FaFileExcel className="me-2 text-success" />
                    Export All to Excel (.xlsx)
                  </Dropdown.Item>
                  <Dropdown.Item onClick={() => exportToExcel("csv")}>
                    <FaFileCsv className="me-2 text-info" />
                    Export All to CSV (.csv)
                  </Dropdown.Item>
                  <Dropdown.Divider />
                  <Dropdown.Item onClick={exportCurrentPage}>
                    <FaDownload className="me-2 text-primary" />
                    Export Current Page Only
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>

              <OverlayTrigger
                placement="bottom"
                overlay={<Tooltip>Refresh</Tooltip>}
              >
                <Button
                  variant="outline-primary"
                  onClick={refreshBenefits}
                  disabled={loading}
                >
                  {loading ? (
                    <Spinner animation="border" size="sm" />
                  ) : (
                    <FaSync />
                  )}
                </Button>
              </OverlayTrigger>

              <OverlayTrigger
                placement="bottom"
                overlay={<Tooltip>Print</Tooltip>}
              >
                <Button variant="outline-secondary" onClick={printBenefits}>
                  <FaPrint />
                </Button>
              </OverlayTrigger>
            </div>
          </div>
        </Col>
      </Row>

      {/* Export Status Alert */}
      {exportLoading && (
        <Alert variant="info" className="mb-3">
          <Spinner animation="border" size="sm" className="me-2" />
          Preparing export file... Please wait.
        </Alert>
      )}

      {/* Statistics Cards */}
      <Row className="mb-4">
        <Col xl={3} md={6} className="mb-3">
          <Card className="stat-card h-100 border-0 shadow-sm bg-primary text-white">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h6 className="card-subtitle mb-2 opacity-75">
                    <FaHandHoldingHeart className="me-1" />
                    Total Benefits
                  </h6>
                  {statsLoading ? (
                    <Spinner animation="border" size="sm" />
                  ) : (
                    <>
                      <h2 className="card-title mb-0">{stats.total}</h2>
                      <p className="card-text mb-0 small opacity-75">
                        All approved
                      </p>
                    </>
                  )}
                </div>
                <div className="display-4 opacity-50">
                  <FaFileAlt />
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col xl={3} md={6} className="mb-3">
          <Card className="stat-card h-100 border-0 shadow-sm bg-success text-white">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h6 className="card-subtitle mb-2 opacity-75">
                    <FaMoneyBillWave className="me-1" />
                    Total Amount
                  </h6>
                  {statsLoading ? (
                    <Spinner animation="border" size="sm" />
                  ) : (
                    <>
                      <h4 className="card-title mb-0">
                        {formatCurrency(stats.totalAmount)}
                      </h4>
                      <p className="card-text mb-0 small opacity-75">
                        Total disbursed
                      </p>
                    </>
                  )}
                </div>
                <div className="display-4 opacity-50">
                  <FaMoneyCheck />
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col xl={3} md={6} className="mb-3">
          <Card className="stat-card h-100 border-0 shadow-sm bg-info text-white">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h6 className="card-subtitle mb-2 opacity-75">
                    <FaPercentage className="me-1" />
                    Average Amount
                  </h6>
                  {statsLoading ? (
                    <Spinner animation="border" size="sm" />
                  ) : (
                    <>
                      <h4 className="card-title mb-0">
                        {formatCurrency(stats.averageAmount)}
                      </h4>
                      <p className="card-text mb-0 small opacity-75">
                        Per benefit
                      </p>
                    </>
                  )}
                </div>
                <div className="display-4 opacity-50">
                  <FaChartBar />
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col xl={3} md={6} className="mb-3">
          <Card className="stat-card h-100 border-0 shadow-sm bg-warning text-white">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h6 className="card-subtitle mb-2 opacity-75">
                    <FaUsers className="me-1" />
                    Total Members
                  </h6>
                  {statsLoading ? (
                    <Spinner animation="border" size="sm" />
                  ) : (
                    <>
                      <h4 className="card-title mb-0">{stats.totalMembers}</h4>
                      <p className="card-text mb-0 small opacity-75">
                        Unique beneficiaries
                      </p>
                    </>
                  )}
                </div>
                <div className="display-4 opacity-50">
                  <FaUsers />
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Filters */}
      <Card className="mb-4 border-0 shadow-sm">
        <Card.Header className="bg-white border-0">
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <Button
                variant="light"
                onClick={() => setShowFilters(!showFilters)}
                className="me-3"
              >
                <FaFilter className="me-2" />
                {showFilters ? "Hide Filters" : "Show Filters"}
              </Button>
              {Object.values(filters).some((val) => val !== "") && (
                <Badge bg="warning" className="me-2">
                  Filters Active
                </Badge>
              )}
            </div>
            <div>
              <InputGroup style={{ width: "300px" }}>
                <InputGroup.Text>
                  <FaSearch />
                </InputGroup.Text>
                <Form.Control
                  placeholder="Search by name, email, phone..."
                  name="search"
                  value={filters.search}
                  onChange={handleFilterChange}
                />
                {filters.search && (
                  <Button
                    variant="outline-secondary"
                    onClick={() =>
                      setFilters((prev) => ({ ...prev, search: "" }))
                    }
                  >
                    Clear
                  </Button>
                )}
              </InputGroup>
            </div>
          </div>
        </Card.Header>

        {showFilters && (
          <Card.Body>
            <Row>
              <Col md={4}>
                <Form.Group>
                  <Form.Label>Benefit Type</Form.Label>
                  <Form.Select
                    name="type"
                    value={filters.type}
                    onChange={handleFilterChange}
                  >
                    <option value="">All Types</option>
                    {Object.entries(benefitTypes).map(([key, type]) => (
                      <option key={key} value={key}>
                        {type.label}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label>From Date</Form.Label>
                  <Form.Control
                    type="date"
                    name="start_date"
                    value={filters.start_date}
                    onChange={handleFilterChange}
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label>To Date</Form.Label>
                  <Form.Control
                    type="date"
                    name="end_date"
                    value={filters.end_date}
                    onChange={handleFilterChange}
                  />
                </Form.Group>
              </Col>
            </Row>
            <div className="mt-3">
              <Button variant="primary" onClick={applyFilters} className="me-2">
                Apply Filters
              </Button>
              <Button variant="outline-secondary" onClick={resetFilters}>
                Reset Filters
              </Button>
            </div>
          </Card.Body>
        )}
      </Card>

      {/* Benefits Table */}
      <Card className="border-0 shadow-sm">
        <Card.Body className="p-0">
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
              <p className="mt-3">Loading benefits...</p>
            </div>
          ) : error ? (
            <Alert variant="danger" className="m-4">
              <FaExclamationTriangle className="me-2" />
              {error}
            </Alert>
          ) : benefits.length === 0 ? (
            <div className="text-center py-5">
              <FaFileAlt className="display-4 text-muted mb-3" />
              <h5>No benefits found</h5>
              <p className="text-muted">
                {Object.values(filters).some((val) => val !== "")
                  ? "Try adjusting your filters"
                  : "No benefits have been added yet"}
              </p>
            </div>
          ) : (
            <>
              <div className="table-responsive">
                <Table hover className="mb-0">
                  <thead className="table-light">
                    <tr>
                      <th
                        style={{ width: "50px", cursor: "pointer" }}
                        onClick={() => handleSort("id")}
                      >
                        <div className="d-flex align-items-center">
                          ID {renderSortIcon("id")}
                        </div>
                      </th>
                      <th
                        style={{ cursor: "pointer" }}
                        onClick={() => handleSort("full_name")}
                      >
                        <div className="d-flex align-items-center">
                          <FaUser className="me-2" />
                          Member {renderSortIcon("full_name")}
                        </div>
                      </th>
                      <th
                        style={{ cursor: "pointer" }}
                        onClick={() => handleSort("type")}
                      >
                        <div className="d-flex align-items-center">
                          Type {renderSortIcon("type")}
                        </div>
                      </th>
                      <th
                        style={{ cursor: "pointer" }}
                        onClick={() => handleSort("amount")}
                      >
                        <div className="d-flex align-items-center">
                          <FaMoneyBillWave className="me-2" />
                          Amount {renderSortIcon("amount")}
                        </div>
                      </th>
                      <th>
                        <div className="d-flex align-items-center">
                          Status
                        </div>
                      </th>
                      <th
                        style={{ cursor: "pointer" }}
                        onClick={() => handleSort("created_at")}
                      >
                        <div className="d-flex align-items-center">
                          <FaCalendar className="me-2" />
                          Date {renderSortIcon("created_at")}
                        </div>
                      </th>
                      <th>Description</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {benefits.map((benefit) => (
                      <tr key={benefit.id}>
                        <td>
                          <Badge bg="secondary">#{benefit.id}</Badge>
                        </td>
                        <td>
                          <div>
                            <strong>{benefit.member_name}</strong>
                            <div className="small text-muted">
                              {benefit.member_email}
                            </div>
                          </div>
                        </td>
                        <td>
                          <Badge
                            bg={benefitTypes[benefit.type]?.color || "secondary"}
                            className="d-inline-flex align-items-center"
                            style={{
                              backgroundColor: benefitTypes[benefit.type]?.bgColor,
                              color: benefitTypes[benefit.type]?.color,
                              border: `1px solid ${benefitTypes[benefit.type]?.color}`,
                            }}
                          >
                            {benefitTypes[benefit.type]?.icon}
                            {benefitTypes[benefit.type]?.label}
                          </Badge>
                        </td>
                        <td>
                          <strong className="text-success">
                            {formatCurrency(benefit.amount)}
                          </strong>
                        </td>
                        <td>
                          <Badge bg="success" className="d-inline-flex align-items-center">
                            <FaCheckCircle className="me-1" />
                            Approved
                          </Badge>
                        </td>
                        <td>
                          <div className="small">
                            {formatDate(benefit.created_at)}
                          </div>
                        </td>
                        <td>
                          <div className="small text-muted" style={{ maxWidth: "200px" }}>
                            {benefit.description || "No description"}
                          </div>
                        </td>
                        <td>
                          <div className="d-flex gap-2">
                            <OverlayTrigger
                              placement="top"
                              overlay={<Tooltip>View Details</Tooltip>}
                            >
                              <Button
                                variant="outline-primary"
                                size="sm"
                                onClick={() => viewBenefitDetails(benefit)}
                              >
                                <FaEye />
                              </Button>
                            </OverlayTrigger>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>

              {/* Pagination */}
              {benefits.length > 0 && (
                <div className="d-flex justify-content-between align-items-center p-3 border-top">
                  <div className="text-muted">
                    Page <strong>{pagination.page}</strong>{" "}
                    {pagination.totalPages > 0 && (
                      <>
                        of <strong>{pagination.totalPages}</strong>
                      </>
                    )}
                    {" • "}
                    Showing <strong>{benefits.length}</strong> benefits
                    {pagination.total > 0 && (
                      <>
                        {" "}
                        (of <strong>{pagination.total}</strong> total)
                      </>
                    )}
                  </div>

                  <div className="d-flex gap-2">
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={() => handlePageChange(pagination.page - 1)}
                      disabled={pagination.page <= 1}
                    >
                      <FaArrowLeft className="me-1" /> Previous
                    </Button>

                    <div className="d-flex align-items-center px-3">
                      <span className="fw-bold">{pagination.page}</span>
                      {pagination.totalPages > 0 && (
                        <span className="text-muted ms-1">
                          / {pagination.totalPages}
                        </span>
                      )}
                    </div>

                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={() => handlePageChange(pagination.page + 1)}
                      disabled={
                        pagination.totalPages === 0 ||
                        pagination.page >= pagination.totalPages
                      }
                    >
                      Next <FaArrowRight className="ms-1" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </Card.Body>
      </Card>

      {/* Benefit Type Statistics Row */}
      {!statsLoading && stats.total > 0 && (
        <Row className="mb-4">
          <Col xs={12}>
            <Card className="border-0 shadow-sm">
              <Card.Header className="bg-white border-0">
                <h6 className="mb-0">
                  <FaChartBar className="me-2 text-primary" />
                  Benefits by Type
                </h6>
              </Card.Header>
              <Card.Body>
                <Row>
                  {Object.entries(stats.byType).map(([type, data]) => (
                    data.count > 0 && (
                      <Col lg={2} md={4} sm={6} key={type} className="mb-3">
                        <div
                          className="p-3 rounded"
                          style={{
                            backgroundColor: benefitTypes[type]?.bgColor,
                            borderLeft: `4px solid ${benefitTypes[type]?.color}`
                          }}
                        >
                          <div className="d-flex align-items-center mb-2">
                            <span className="me-2" style={{ color: benefitTypes[type]?.color }}>
                              {benefitTypes[type]?.icon}
                            </span>
                            <strong style={{ color: benefitTypes[type]?.color }}>
                              {benefitTypes[type]?.label}
                            </strong>
                          </div>
                          <div className="small">
                            <div>Count: <strong>{data.count}</strong></div>
                            <div>Amount: <strong>{formatCurrency(data.amount)}</strong></div>
                            <div className="text-muted">
                              {((data.count / stats.total) * 100).toFixed(1)}% of total
                            </div>
                          </div>
                        </div>
                      </Col>
                    )
                  ))}
                </Row>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}

      {/* Benefit Detail Modal */}
      <Modal
        show={showDetailModal}
        onHide={() => setShowDetailModal(false)}
        size="lg"
      >
        <Modal.Header closeButton className="bg-primary text-white">
          <Modal.Title>
            <FaFileAlt className="me-2" />
            Benefit Details
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedBenefit && (
            <Row>
              <Col md={6}>
                <h6 className="text-muted mb-3">BENEFIT INFORMATION</h6>
                <div className="mb-3">
                  <small className="text-muted">Benefit ID</small>
                  <p className="fw-bold">#{selectedBenefit.id}</p>
                </div>
                <div className="mb-3">
                  <small className="text-muted">Type</small>
                  <p>
                    <Badge
                      bg={benefitTypes[selectedBenefit.type]?.color || "secondary"}
                      className="d-inline-flex align-items-center"
                      style={{
                        backgroundColor: benefitTypes[selectedBenefit.type]?.bgColor,
                        color: benefitTypes[selectedBenefit.type]?.color,
                      }}
                    >
                      {benefitTypes[selectedBenefit.type]?.icon}
                      {benefitTypes[selectedBenefit.type]?.label}
                    </Badge>
                  </p>
                </div>
                <div className="mb-3">
                  <small className="text-muted">Amount</small>
                  <h4 className="text-success">
                    {formatCurrency(selectedBenefit.amount)}
                  </h4>
                </div>
                <div className="mb-3">
                  <small className="text-muted">Status</small>
                  <p>
                    <Badge bg="success" className="d-inline-flex align-items-center">
                      <FaCheckCircle className="me-1" />
                      Approved
                    </Badge>
                  </p>
                </div>
              </Col>
              <Col md={6}>
                <h6 className="text-muted mb-3">MEMBER INFORMATION</h6>
                <div className="mb-3">
                  <small className="text-muted">Member Name</small>
                  <p className="fw-bold">{selectedBenefit.member_name}</p>
                </div>
                <div className="mb-3">
                  <small className="text-muted">Email</small>
                  <p>{selectedBenefit.member_email}</p>
                </div>
                <div className="mb-3">
                  <small className="text-muted">Phone</small>
                  <p>{selectedBenefit.member_phone}</p>
                </div>
                <div className="mb-3">
                  <small className="text-muted">Member ID</small>
                  <p>{selectedBenefit.member_custom_id || "N/A"}</p>
                </div>
              </Col>

              {/* Dependent Information Section */}
              {selectedBenefit.dependent && (
                <>
                  <Col md={12}>
                    <hr />
                    <h6 className="text-muted mb-3">
                      <FaChild className="me-2" />
                      DEPENDENT INFORMATION
                    </h6>
                  </Col>
                  <Col md={6}>
                    <div className="mb-3">
                      <small className="text-muted">Dependent Name</small>
                      <p className="fw-bold">{selectedBenefit.dependent.name}</p>
                    </div>
                    <div className="mb-3">
                      <small className="text-muted">Relationship</small>
                      <p>
                        <Badge bg="info" className="d-inline-flex align-items-center">
                          <FaUser className="me-1" />
                          {selectedBenefit.dependent.relationship}
                        </Badge>
                      </p>
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="mb-3">
                      <small className="text-muted">Date of Birth</small>
                      <p>
                        {selectedBenefit.dependent.date_of_birth
                          ? new Date(selectedBenefit.dependent.date_of_birth).toLocaleDateString("en-GB", {
                            day: "2-digit",
                            month: "long",
                            year: "numeric"
                          })
                          : "N/A"}
                      </p>
                    </div>
                    <div className="mb-3">
                      <small className="text-muted">Age</small>
                      <p>
                        {selectedBenefit.dependent.age !== null
                          ? `${selectedBenefit.dependent.age} years`
                          : "N/A"}
                      </p>
                    </div>
                  </Col>
                </>
              )}

              <Col md={12}>
                <hr />
                <h6 className="text-muted mb-3">PROCESSING INFORMATION</h6>
                <Row>
                  <Col md={4}>
                    <small className="text-muted">Created By</small>
                    <p>{selectedBenefit.created_by_name || "N/A"}</p>
                  </Col>
                  <Col md={4}>
                    <small className="text-muted">Approved By</small>
                    <p>{selectedBenefit.approved_by_name || "System Auto-approval"}</p>
                  </Col>
                  <Col md={4}>
                    <small className="text-muted">Approved At</small>
                    <p>
                      {selectedBenefit.approved_at
                        ? formatDate(selectedBenefit.approved_at)
                        : formatDate(selectedBenefit.created_at)}
                    </p>
                  </Col>
                </Row>
                <Row>
                  <Col md={6}>
                    <small className="text-muted">Created At</small>
                    <p>{formatDate(selectedBenefit.created_at)}</p>
                  </Col>
                  <Col md={6}>
                    <small className="text-muted">Description</small>
                    <p className="text-muted">{selectedBenefit.description || "No description provided"}</p>
                  </Col>
                </Row>
              </Col>
            </Row>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDetailModal(false)}>
            Close
          </Button>
          <Button
            variant="primary"
            onClick={() => {
              const exportData = [selectedBenefit];
              const exportFormatted = formatDataForExport(exportData);
              const wb = XLSX.utils.book_new();
              const ws = XLSX.utils.json_to_sheet(exportFormatted);
              XLSX.utils.book_append_sheet(wb, ws, "Benefit_Details");
              XLSX.writeFile(wb, `Benefit_${selectedBenefit.id}_Details.xlsx`);
              toast.success("Benefit details exported");
            }}
          >
            <FaDownload className="me-2" />
            Export Details
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default BenefitsList;